import { pick, extend, omit, isString, isEmpty, has } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import {
  modelToViewModelForLocationTargets,
  modelToViewModelForDayTimeTargets,
  bulkModelToViewModelForLocationTargets,
  bulkModelToViewModelForDayTimeTargets,
  modelToViewModelForTargetCriterions,
  modelToViewModelForAudienceCriterions,
  AdGroupNegativeReferenceId,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { bulkModelToViewModelForProductPartitions } from './ad-group-criterions';

// The i18n object passed from the React context is not fully initialized with account timezone.
// The parseDate and formatDate will use the browser timezone instead of account timezone, causing issues.
const globalLegacyI18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/campaign-creation-advanced');

const whitelistedSchemaPropertyKeysForModel = [
  'Id', 'Name', 'Status', 'BidStrategyName', 'Campaign', 'SearchBid', 'CampaignName',
  'AdScheduleUseSearcherTimeZone', 'StartDate', 'EndDate', 'FrequencyCapSettings',
  'TrackingUrlTemplate', 'UrlCustomParameters', 'FinalUrlSuffix', 'UseOptimizedTargeting',
  'AdDistribution', 'Medium',
];

const readonlyPropertyKeysForModel = [
  // StartDate is not simply readonly. It can be updated in certain situations such as the current start date being in the future
  // Refer to the DB sproc for details.
  // 'StartDate',
];

export const modelToViewModelForAdGroup = ({
  adGroupModel,
  campaignModel,
  locationCriterionsModel,
  dayTimeCriterionsModel,
  bundleCountries,
  targetCriterionsModel,
  audienceCriterionsModel,
  i18n,
  isAudienceShoppingCampaign = false,
}) => {
  let data = pick(adGroupModel, whitelistedSchemaPropertyKeysForModel);

  data.CampaignSubType = campaignModel && campaignModel.CampaignSubType;
  data.CampaignBidStrategyName = campaignModel && campaignModel.BiddingScheme && campaignModel.BiddingScheme.Type;
  data.AdRotation = adGroupModel.AdRotation && adGroupModel.AdRotation.RotationType;
  data.ProductPartitions = adGroupModel.ProductPartitions;

  if (!isAudienceShoppingCampaign) {
    data.AdDistribution = adGroupModel.AdDistribution;
  }

  data = extend(data, modelToViewModelForDateSetting({
    adGroupModel,
    i18n,
  }));

  if (isAudienceShoppingCampaign) {
    data.UseOptimizedTargeting = adGroupModel.UseOptimizedTargeting;
  }

  if (locationCriterionsModel) {
    data = extend(data, modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      bundleCountries,
      adGroupId: adGroupModel.Id,
    }));
  }

  if (dayTimeCriterionsModel) {
    data = extend(data, modelToViewModelForDayTimeTargets({
      model: dayTimeCriterionsModel,
    }));
  }

  if (targetCriterionsModel) {
    data = extend(data, modelToViewModelForTargetCriterions({
      model: targetCriterionsModel,
    }));
  }

  if (audienceCriterionsModel) {
    data = extend(data, modelToViewModelForAudienceCriterions({
      model: audienceCriterionsModel,
    }));
  }

  const adGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return adGroupViewModel;
};

export const convertMachineDateToAccountDate = (date) => {
  const dateString = globalLegacyI18n.formatDate(date, {
    timeZone: undefined,
    raw: 'yyyy-MM-dd',
  });

  return globalLegacyI18n.parseDate(dateString, {
    timeZone: undefined,
    raw: 'yyyy-MM-dd',
  });
};

const formatDateToAPIFormat = (i18n, date) => {
  if (date == null) {
    return '';
  }

  if (typeof date === 'string') {
    return date;
  }

  return globalLegacyI18n.formatDate(date, {
    raw: 'M/d/yyyy',
  });
};

const parseDateFromAPIFormat = (i18n, date) => {
  if (date == null) {
    return null;
  }

  if (typeof date === 'string') {
    const dateFromMdyyyyFormat = globalLegacyI18n.parseDate(date.substring(0, 10), { raw: 'M/d/yyyy' });
    const dateFromyyyyMdFormat = globalLegacyI18n.parseDate(date.substring(0, 10), { raw: 'yyyy-M-d' });
    return dateFromMdyyyyFormat || dateFromyyyyMdFormat;
  }

  return date;
};

export const modelToViewModelForDateSetting = ({ adGroupModel, i18n }) => ({
  StartDate: adGroupModel.StartDate ? parseDateFromAPIFormat(i18n, adGroupModel.StartDate) : undefined,
  EndDate: adGroupModel.EndDate ? parseDateFromAPIFormat(i18n, adGroupModel.EndDate) : undefined,
});

export const viewModelToBulkModelForDateSetting = ({ i18n, adGroupViewModel }) => ({
  StartDate: formatDateToAPIFormat(i18n, adGroupViewModel.data.StartDate),
  EndDate: adGroupViewModel.data.EndDate ? formatDateToAPIFormat(i18n, adGroupViewModel.data.EndDate) : '1/1/3000',
});

const bulkModelToViewModelForDateSetting = ({ adGroupModel, originalAdGroupViewModel, i18n }) => ({
  StartDate: adGroupModel.StartDate ? parseDateFromAPIFormat(i18n, adGroupModel.StartDate) : originalAdGroupViewModel.data.StartDate,
  EndDate: adGroupModel.EndDate && adGroupModel.EndDate !== '1/1/3000' ? parseDateFromAPIFormat(i18n, adGroupModel.EndDate) : undefined,
});

export const bulkModelToViewModelForAdGroup = ({
  adGroupModel,
  bundleCountries,
  originalAdGroupViewModel,
  i18n,
}) => {
  let data = pick(adGroupModel, whitelistedSchemaPropertyKeysForModel);
  if (data.Id && isString(data.Id)) {
    // eslint-disable-next-line no-param-reassign
    data.Id = parseInt(data.Id, 10);
  }

  data = extend(data, bulkModelToViewModelForLocationTargets({
    locationTargetsModel: adGroupModel.AdGroupCriterions || [],
    bundleCountries,
  }));
  data = extend(data, bulkModelToViewModelForDayTimeTargets({
    criterions: adGroupModel.AdGroupCriterions || [],
  }));

  data = extend(data, bulkModelToViewModelForDateSetting({ adGroupModel, originalAdGroupViewModel, i18n }));

  if (isEmpty(data.FrequencyCapSettings)) {
    data = extend(data, { FrequencyCapSettings: [{}] });
  }

  if (originalAdGroupViewModel.data.deal) {
    data = extend(data, {
      deal: originalAdGroupViewModel.data.deal,
    });
  }

  data = extend(data, bulkModelToViewModelForProductPartitions({
    adGroupModel,
  }));

  const adGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return adGroupViewModel;
};

export const viewModelToModelForAdGroup = ({ adGroupViewModel, i18n, isAudienceShoppingCampaign }) => {
  let model = pick(adGroupViewModel.data, whitelistedSchemaPropertyKeysForModel);

  model = extend(model, viewModelToBulkModelForDateSetting({ i18n, adGroupViewModel }));

  if (!adGroupViewModel.data.Id || adGroupViewModel.data.Id === AdGroupNegativeReferenceId) {
    model.Id = AdGroupNegativeReferenceId;

    if (isEmpty(model.FrequencyCapSettings) ||
      isEmpty(model.FrequencyCapSettings[0]) ||
      !model.FrequencyCapSettings[0].CapValue) {
      model = omit(model, 'FrequencyCapSettings');
    }
  } else {
    model = omit(model, readonlyPropertyKeysForModel);
  }

  if (has(adGroupViewModel.data, 'PricingModel')) {
    model.PricingModel = adGroupViewModel.data.PricingModel;
  }

  if (isAudienceShoppingCampaign) {
    model = omit(model, 'AdDistribution');
  } else if (has(adGroupViewModel.data, 'AdDistribution')) {
    model.AdDistribution = adGroupViewModel.data.AdDistribution;
  }

  if (has(adGroupViewModel.data, 'AdRotation')) {
    model.AdRotation = adGroupViewModel.data.AdRotation;
  }

  if (has(adGroupViewModel.data, 'TargetSetting')) {
    model.TargetSetting = adGroupViewModel.data.TargetSetting;
  }

  if (adGroupViewModel.data.CampaignBidStrategyName === 'ManualCpc' || adGroupViewModel.data.CampaignBidStrategyName === 'EnhancedCpc') {
    model.SearchBid = adGroupViewModel.data.SearchBid;
  }

  return model;
};


const readAdGroup = (cid, aid, campaignId, adGroupId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .get()
    .toPromise();

export const loadAdGroup = (cid, aid, campaignId, adGroupId) =>
  readAdGroup(cid, aid, campaignId, adGroupId);
